import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import type { PageItem, RecordItem, AttachmentItem } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useSystemBranding } from '../../context/SystemBrandingContext';
import { ThemeToggle } from '../common/ThemeToggle';
import { naturalCompare } from '../../utils/sort';
import { BatchUploadModal } from '../transparency/BatchUploadModal';
import { ScrollToTopButton } from '../common/ScrollToTopButton';
import { RichTextView } from '../common/RichTextView';
import { PublicArchiveSection } from './PublicArchiveSection';
import { PersonnelDataProvider, hasPersonnelToken, type LiveBundle, type PersonnelData } from '../personnel/PersonnelBlock';
import { fromPublic, ORG_CHART_DEFAULTS } from '../personnel/orgModel';
import { buildPointTree, type PointNode } from '../../utils/pointTree';
import { usePersistedViewMode } from '../../hooks/usePersistedViewMode';
import { 
  Search, 
  Download, 
  ArrowLeft, 
  ChevronDown, 
  ChevronRight, 
  Paperclip, 
  Layers, 
  Table as TableIcon, 
  RefreshCw, 
  Share2, 
  Check, 
  ShieldCheck, 
  Upload,
  Trash2,
  ExternalLink,
  ListTree,
  FolderTree,
  Rows3,
  Grid3x3,
  Square,
  Save,
  Filter,
  FolderOpen,
  BookOpen,
  Calendar,
  ArrowUpDown
} from 'lucide-react';

function parseRecordData(rec: RecordItem): Record<string, any> {
  if (rec.parsedData && typeof rec.parsedData === 'object') return rec.parsedData;
  if (typeof rec.data === 'string') {
    try {
      return JSON.parse(rec.data || '{}');
    } catch {
      return {};
    }
  }
  if (rec.data && typeof rec.data === 'object') return rec.data;
  return {};
}

interface PublicPortalViewProps {
  onBackToAdmin?: () => void;
}

export const PublicPortalView: React.FC<PublicPortalViewProps> = ({ onBackToAdmin }) => {
  const { user, tenant: authTenant } = useAuth();
  const { branding } = useSystemBranding();
  const isAdmin = Boolean(user || ApiClient.getToken());

  // Segédfüggvény: Route paraméterek feloldása (támogatja: kpub.domain.hu/{slug}/{page}, /public/{slug}, ?t={slug}&p={page})
  const getInitialRouteState = () => {
    const sp = new URLSearchParams(window.location.search);
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    const segments = path ? path.split('/').filter(Boolean) : [];
    
    let slug = sp.get('t') || '';
    let page = sp.get('p') || '';
    const block = sp.get('block') || '';

    if (segments.length > 0) {
      if (segments[0] === 'public' || segments[0] === 'kpub') {
        if (segments[1]) {
          slug = slug || segments[1];
          if (segments[2]) page = page || segments[2];
        }
      } else {
        const reserved = ['login', 'admin', 'app', 'tenants', 'reset-password', 'help', 'api'];
        if (!reserved.includes(segments[0])) {
          slug = slug || segments[0];
          if (segments[1]) page = page || segments[1];
        }
      }
    }

    return {
      slug: slug || authTenant?.slug || 'gyal',
      page,
      block,
    };
  };

  const initialRoute = getInitialRouteState();
  const initialSlug = initialRoute.slug;
  const initialPage = initialRoute.page;
  const initialBlock = initialRoute.block;

  const [tenantSlug, setTenantSlug] = useState<string>(initialSlug);
  const [selectedPageSlug, setSelectedPageSlug] = useState<string>(initialPage);
  const [availableTenants, setAvailableTenants] = useState<{ slug: string; name: string }[]>([]);

  // Data State
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<any>(null);
  const [pages, setPages] = useState<(PageItem & { description?: string; icon?: string })[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Page Detail State
  const [pageDetailLoading, setPageDetailLoading] = useState(false);
  const [activePage, setActivePage] = useState<PageItem | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [records, setRecords] = useState<RecordItem[]>([]);
  const [personnelRaw, setPersonnelRaw] = useState<any>(null);
  const [pageSearch, setPageSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  // alpontok szerinti bontás (pl. 2.4 › Adóigazgatás) – alapértelmezés; az egyszerű lista választható
  const [hierarchyMode, setHierarchyMode] = usePersistedViewMode<'tree' | 'flat'>('public-hierarchy', 'tree', ['tree', 'flat']);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  // táblázat rácsvonalai: a látogató átállíthatja; az admin a „Nézet mentése” gombbal az oldalhoz menti
  const [gridOverride, setGridOverride] = useState<'rows' | 'grid' | 'none' | null>(null);
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(initialBlock || null);
  
  // View Mode: default is 'accordion' (kinyíló listás nézet), de megőrizzük a böngészőben
  const [viewModeOverride, setViewModeOverrideState] = useState<'accordion' | 'cards' | 'table' | null>(() => {
    try {
      const saved = localStorage.getItem('kozat_view_mode_public_portal');
      if (saved && ['accordion', 'cards', 'table'].includes(saved)) {
        return saved as 'accordion' | 'cards' | 'table';
      }
    } catch {}
    return null;
  });

  const setViewModeOverride = (mode: 'accordion' | 'cards' | 'table' | null) => {
    setViewModeOverrideState(mode);
    try {
      if (mode) {
        localStorage.setItem('kozat_view_mode_public_portal', mode);
      } else {
        localStorage.removeItem('kozat_view_mode_public_portal');
      }
    } catch {}
  };

  const [isSavingViewMode, setIsSavingViewMode] = useState(false);
  const [savedModeSuccess, setSavedModeSuccess] = useState(false);

  // Batch Upload Modal
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [uploadingRecordId, setUploadingRecordId] = useState<number | null>(null);

  // Year Search State
  const [availableYearsWithCount, setAvailableYearsWithCount] = useState<{ year: string; count: number }[]>([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [yearSearchResults, setYearSearchResults] = useState<{ page: any; rec: any; d: any }[]>([]);
  const [yearSearchLoading, setYearSearchLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Page Detail Year Filter & Sort
  const [pageSelectedYear, setPageSelectedYear] = useState<string>('all');
  const [pageSortOrder, setPageSortOrder] = useState<'pont' | 'year_desc' | 'year_asc'>('pont');

  useEffect(() => {
    ApiClient.getTenants().then(res => {
      if (res.ok && res.data) {
        setAvailableTenants(res.data.filter((t: any) => t.slug !== 'system'));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (authTenant?.slug && !sp.get('t') && tenantSlug !== authTenant.slug) {
      setTenantSlug(authTenant.slug);
    }
  }, [authTenant?.slug]);

  // Load Tenant Overview
  const loadTenantOverview = async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await ApiClient.getPublicTenant(slug);
      if (res.ok && res.data) {
        setTenant(res.data.tenant);
        setPages(res.data.pages || []);
        collectYears(slug, res.data.pages || []);
      } else {
        setError(res.error || 'A közzétételi lista jelenleg nem érhető el.');
      }
    } catch (e: any) {
      setError(e.message || 'Hálózati hiba');
    } finally {
      setLoading(false);
    }
  };

  // Collect years across published pages for year search box - ONLY keep years with count > 0
  const collectYears = async (slug: string, publishedPages: PageItem[]) => {
    setYearSearchLoading(true);
    const yearCounts = new Map<string, number>();
    const pageRecordsCollector: { page: any; records: any[] }[] = [];

    for (const p of publishedPages) {
      try {
        const res = await ApiClient.getPublicPage(slug, p.slug);
        if (res.ok && res.data?.records) {
          pageRecordsCollector.push({ page: res.data.page, records: res.data.records });
          res.data.records.forEach((rec: any) => {
            const matchedYearsForRecord = new Set<string>();
            if (rec.year) {
              matchedYearsForRecord.add(String(rec.year));
            }
            let d = rec.data;
            if (typeof d === 'string') {
              try { d = JSON.parse(d); } catch {}
            }
            if (d && typeof d === 'object') {
              Object.values(d).forEach(val => {
                const match = String(val ?? '').match(/\b(19|20)\d{2}\b/);
                if (match) matchedYearsForRecord.add(match[0]);
              });
            }
            matchedYearsForRecord.forEach(yr => {
              yearCounts.set(yr, (yearCounts.get(yr) || 0) + 1);
            });
          });
        }
      } catch {}
    }

    const validYears = Array.from(yearCounts.entries())
      .filter(([_, count]) => count > 0)
      .map(([year, count]) => ({ year, count }))
      .sort((a, b) => Number(b.year) - Number(a.year));

    setAvailableYearsWithCount(validYears);
    (window as any)._cachedPublicPageRecords = pageRecordsCollector;
    setYearSearchLoading(false);
  };

  // Run Year Filter / Search - matches both structured year and data content
  const runYearFilter = (targetYear: string) => {
    setSelectedYear(targetYear);
    if (!targetYear) {
      setYearSearchResults([]);
      return;
    }

    const collector: { page: any; records: any[] }[] = (window as any)._cachedPublicPageRecords || [];
    const matches: { page: any; rec: any; d: any }[] = [];

    collector.forEach(({ page, records }) => {
      records.forEach(rec => {
        let d = rec.data;
        if (typeof d === 'string') {
          try { d = JSON.parse(d); } catch {}
        }
        const matchesStructured = rec.year && String(rec.year) === targetYear;
        let matchesText = false;
        if (d && typeof d === 'object') {
          matchesText = Object.values(d).some(val => {
            const m = String(val ?? '').match(/\b(19|20)\d{2}\b/);
            return m && m[0] === targetYear;
          });
        }
        if (matchesStructured || matchesText) {
          matches.push({ page, rec, d });
        }
      });
    });

    setYearSearchResults(matches);
  };

  // Load Single Page Details
  const loadPageDetail = async (slug: string, pSlug: string) => {
    setPageDetailLoading(true);
    try {
      const res = await ApiClient.getPublicPage(slug, pSlug);
      if (res.ok && res.data) {
        setActivePage(res.data.page);
        setFields(res.data.fields || []);
        setPersonnelRaw({ own: (res.data as any).personnel || null, entities: (res.data as any).entity_personnel || null, linked: (res.data as any).linked || null });
        
        // Sort records naturally by pont
        const sortedRecs = [...(res.data.records || [])].sort((a, b) => {
          let da = a.data;
          let db = b.data;
          if (typeof da === 'string') { try { da = JSON.parse(da); } catch {} }
          if (typeof db === 'string') { try { db = JSON.parse(db); } catch {} }
          return naturalCompare(da?.pont || String(a.sort_order || ''), db?.pont || String(b.sort_order || ''));
        });

        setRecords(sortedRecs);
        
        // Auto-expand nodes: if block specified in URL or first 2 items
        const initialExpanded: Record<string, boolean> = {};
        if (initialBlock) {
          initialExpanded[initialBlock] = true;
        } else {
          // By default expand first item
          if (sortedRecs.length > 0) {
            initialExpanded[String(sortedRecs[0].id)] = true;
          }
        }
        setExpandedNodes(initialExpanded);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setPageDetailLoading(false);
    }
  };

  useEffect(() => {
    loadTenantOverview(tenantSlug);
  }, [tenantSlug]);

  useEffect(() => {
    if (selectedPageSlug) {
      loadPageDetail(tenantSlug, selectedPageSlug);
    } else {
      setActivePage(null);
      setRecords([]);
      setFields([]);
      setFocusedBlockId(null);
    }
  }, [selectedPageSlug, tenantSlug]);

  // URL szinkronizálása a címsorban anélkül, hogy újratöltené az oldalt
  useEffect(() => {
    try {
      const isKpub = window.location.hostname.includes('kpub');
      const url = new URL(window.location.href);
      if (isKpub) {
        url.pathname = `/${encodeURIComponent(tenantSlug)}`;
        url.searchParams.delete('t');
      } else {
        url.searchParams.set('t', tenantSlug);
      }
      if (selectedPageSlug) url.searchParams.set('p', selectedPageSlug);
      else url.searchParams.delete('p');
      if (focusedBlockId) url.searchParams.set('block', focusedBlockId);
      else url.searchParams.delete('block');

      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch {}
  }, [tenantSlug, selectedPageSlug, focusedBlockId]);

  const handleCopyLink = () => {
    const url = new URL(window.location.href);
    const isKpub = window.location.hostname.includes('kpub');
    if (isKpub) {
      url.pathname = `/${encodeURIComponent(tenantSlug)}`;
      url.searchParams.delete('t');
    } else {
      url.searchParams.set('t', tenantSlug);
    }
    if (selectedPageSlug) url.searchParams.set('p', selectedPageSlug);
    else url.searchParams.delete('p');
    if (focusedBlockId) url.searchParams.set('block', focusedBlockId);
    else url.searchParams.delete('block');

    navigator.clipboard.writeText(url.toString());
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleSaveDefaultViewMode = async () => {
    if (!activePage) return;
    setIsSavingViewMode(true);
    try {
      const mode = effectiveDisplayMode;
      const grid = effectiveTableGrid;
      const res = await ApiClient.updatePage(activePage.id, { display_mode: mode, table_grid: grid });
      if (res.ok) {
        setActivePage(prev => prev ? { ...prev, display_mode: mode, table_grid: grid } : null);
        setGridOverride(null);
        setSavedModeSuccess(true);
        setTimeout(() => setSavedModeSuccess(false), 2500);
      } else {
        alert(res.error || 'Hiba a nézet mentésekor');
      }
    } catch (e: any) {
      alert(e.message || 'Hiba');
    } finally {
      setIsSavingViewMode(false);
    }
  };

  const handleDirectUpload = async (recordId: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingRecordId(recordId);
    try {
      const res = await ApiClient.uploadRecordAttachments(recordId, Array.from(files));
      if (res.ok) {
        await loadPageDetail(tenantSlug, selectedPageSlug);
      } else {
        alert(res.error || 'Hiba történt a feltöltés során');
      }
    } catch (err: any) {
      alert(err.message || 'Hiba a feltöltés során');
    } finally {
      setUploadingRecordId(null);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number, filename: string) => {
    if (!confirm(`Biztosan törli a(z) "${filename}" csatolt fájlt?`)) return;
    try {
      const res = await ApiClient.deleteAttachment(attachmentId);
      if (res.ok) {
        await loadPageDetail(tenantSlug, selectedPageSlug);
      } else {
        alert(res.error || 'Hiba a csatolmány törlésekor');
      }
    } catch (err: any) {
      alert(err.message || 'Hiba');
    }
  };

  const brandColor = tenant?.brand_color || '#1a5fd4';
  const matchesWithAttachments = yearSearchResults.filter(m => m.rec.attachment_count > 0);

  // Egy rekordhoz tartozó tényleges év felderítése:
  const getRecordEffectiveYear = React.useCallback((rec: any): number | null => {
    if (rec.year && rec.year >= 2010 && rec.year <= 2035) {
      return rec.year;
    }

    if (rec.attachments && rec.attachments.length > 0) {
      const attYears: number[] = [];
      rec.attachments.forEach((att: any) => {
        const fn = `${att.filename || ''} ${att.name || ''} ${att.original_name || ''}`;
        const m = fn.match(/\b(20[123]\d)\b/g);
        if (m) {
          m.forEach((yrStr: string) => {
            const yNum = parseInt(yrStr, 10);
            if (yNum >= 2010 && yNum <= 2035) attYears.push(yNum);
          });
        }
      });
      if (attYears.length > 0) {
        return Math.max(...attYears);
      }
    }

    const dataStr = JSON.stringify(rec.parsedData || rec.data || {});
    const m = dataStr.match(/\b(20[123]\d)\b/g);
    if (m && m.length > 0) {
      const dataYears = m.map((y: string) => parseInt(y, 10)).filter((y: number) => y >= 2010 && y <= 2035);
      if (dataYears.length > 0) {
        return Math.max(...dataYears);
      }
    }

    return null;
  }, []);

  const isRecordForYear = React.useCallback((rec: any, yr: number) => {
    const eff = getRecordEffectiveYear(rec);
    if (eff === yr) return true;

    const yrStr = String(yr);
    if (rec.attachments && rec.attachments.some((att: any) => {
      const fn = `${att.filename || ''} ${att.name || ''} ${att.original_name || ''}`;
      return fn.includes(yrStr);
    })) {
      return true;
    }

    const dataStr = JSON.stringify(rec.parsedData || rec.data || {});
    return dataStr.includes(yrStr);
  }, [getRecordEffectiveYear]);

  const pageAvailableYears = React.useMemo(() => {
    const s = new Set<number>();
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 4; y--) {
      s.add(y);
    }
    records.forEach(r => {
      const eff = getRecordEffectiveYear(r);
      if (eff) s.add(eff);

      if (r.attachments) {
        r.attachments.forEach((att: any) => {
          const fn = `${att.filename || ''} ${att.name || ''} ${att.original_name || ''}`;
          const m = fn.match(/\b(20[123]\d)\b/g);
          if (m) {
            m.forEach((yrStr: string) => {
              const yNum = parseInt(yrStr, 10);
              if (yNum >= 2010 && yNum <= 2035) s.add(yNum);
            });
          }
        });
      }

      const dataStr = JSON.stringify(r.parsedData || r.data || {});
      const m = dataStr.match(/\b(20[123]\d)\b/g);
      if (m) {
        m.forEach((yrStr: string) => {
          const yNum = parseInt(yrStr, 10);
          if (yNum >= 2010 && yNum <= 2035) s.add(yNum);
        });
      }
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [records, getRecordEffectiveYear]);

  const pageSelectableYears = React.useMemo(() => {
    return pageAvailableYears
      .map(yr => ({
        year: yr,
        count: records.filter(r => isRecordForYear(r, yr)).length
      }))
      .filter(item => item.count > 0);
  }, [pageAvailableYears, records, isRecordForYear]);


  // Filtered records
  const filteredRecords = records.filter(rec => {
    if (pageSelectedYear !== 'all') {
      if (pageSelectedYear === 'timeless') {
        if (rec.year) return false;
      } else {
        const yrNum = Number(pageSelectedYear);
        if (!isRecordForYear(rec, yrNum)) return false;
      }
    }
    const d = parseRecordData(rec);
    if (focusedBlockId && String(rec.id) !== focusedBlockId && d.pont !== focusedBlockId) {
      return false;
    }
    if (!pageSearch) return true;
    const searchLower = pageSearch.toLowerCase();
    return Object.values(d || {}).some(v => String(v ?? '').toLowerCase().includes(searchLower));
  });

  // Sorted records (respects chronological sorting or point order)
  const sortedRecords = React.useMemo(() => {
    const list = [...filteredRecords];
    list.sort((a, b) => {
      if (pageSortOrder === 'year_desc') {
        const ya = getRecordEffectiveYear(a) ?? -1;
        const yb = getRecordEffectiveYear(b) ?? -1;
        if (ya !== yb) return yb - ya;
      } else if (pageSortOrder === 'year_asc') {
        const ya = getRecordEffectiveYear(a) ?? 9999;
        const yb = getRecordEffectiveYear(b) ?? 9999;
        if (ya !== yb) return ya - yb;
      }
      const da = parseRecordData(a);
      const db = parseRecordData(b);
      return naturalCompare(da?.pont || String(a.sort_order || ''), db?.pont || String(b.sort_order || ''));
    });
    return list;
  }, [filteredRecords, pageSortOrder]);

  // Effective display mode defaults to 'accordion' (kinyíló listás nézet)
  const effectiveDisplayMode = viewModeOverride || activePage?.display_mode || 'accordion';
  const effectiveTableGrid: 'rows' | 'grid' | 'none' = gridOverride || activePage?.table_grid || 'rows';

  const toggleNode = (nodeId: string) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  // ── Alpontok szerinti megjelenítés (mindhárom nézetben) ──
  // A csoport (pl. „Adóigazgatás”) a szülőpont (2.4) alatt, behúzva, lenyitható sávként jelenik meg;
  // kereséskor a találatot tartalmazó csoportok nyitva vannak.
  const isGroupOpen = (key: string, kind: 'accordion' | 'cards' | 'table') =>
    openGroups[key] ?? (pageSearch.trim() ? true : kind === 'table');
  const toggleGroup = (key: string, kind: 'accordion' | 'cards' | 'table') =>
    setOpenGroups((prev) => ({ ...prev, [key]: !isGroupOpen(key, kind) }));

  const renderGrouped = (
    items: RecordItem[],
    kind: 'accordion' | 'cards' | 'table',
    renderItem: (rec: RecordItem, nested: boolean, subInfo?: { groups: number; items: number }, inner?: React.ReactNode) => React.ReactNode,
  ): React.ReactNode => {
    if (hierarchyMode === 'flat') return items.map((r) => renderItem(r, false));
    const tree: PointNode<RecordItem>[] = buildPointTree(items, parseRecordData, (r) => r.id);
    return tree.map((n) => {
      const subInfo = n.groups.length ? { groups: n.groups.length, items: n.groups.reduce((a, g) => a + g.items.length, 0) } : undefined;
      // lista nézetben az alpontok a főpont részei: csak a főpont lenyitásakor látszanak (kereséskor mindig)
      const vpKey = `vp|${n.key}`;
      const parentOpen = kind !== 'accordion' || pageSearch.trim() !== ''
        || (n.rec ? (expandedNodes[String(n.rec.id)] ?? false) : !!openGroups[vpKey]);
      const head = n.rec
        ? renderItem(n.rec, false, subInfo)
        : kind === 'table'
          ? <tr key={`vp-${n.key}`} className="bg-slate-50 dark:bg-slate-950/50"><td colSpan={5} className="px-4 py-2.5 font-semibold text-slate-800 dark:text-slate-200">{n.label}</td></tr>
          : (
            <button key={`vp-${n.key}`} type="button" aria-expanded={parentOpen}
              onClick={() => kind === 'accordion' && setOpenGroups((prev) => ({ ...prev, [vpKey]: !prev[vpKey] }))}
              className="w-full px-4 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-between gap-3 text-left" style={{ backgroundColor: brandColor }}>
              <span>{n.label}</span>
              {kind === 'accordion' && (parentOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
            </button>
          );
      if (!parentOpen) return <React.Fragment key={`n-${n.key}`}>{head}</React.Fragment>;
      const groups = n.groups.map((g) => {
        const open = isGroupOpen(g.key, kind);
        const count = g.items.length;
        if (kind === 'table') {
          return (
            <React.Fragment key={`g-${g.key}`}>
              <tr onClick={() => toggleGroup(g.key, kind)} className="cursor-pointer select-none bg-blue-50/50 hover:bg-blue-50 dark:bg-blue-950/20 dark:hover:bg-blue-950/40" aria-expanded={open}>
                <td colSpan={5} className="py-2 pr-4" style={{ paddingLeft: 32 }}>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-800 dark:text-cyan-300">
                    {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    <FolderTree className="w-3.5 h-3.5" /> {g.label}
                    <span className="font-normal text-slate-500 dark:text-slate-400">· {count} tétel</span>
                  </span>
                </td>
              </tr>
              {open && g.items.map((r) => renderItem(r, true))}
            </React.Fragment>
          );
        }
        return (
          <div key={`g-${g.key}`} className="space-y-2">
            <button
              type="button"
              onClick={() => toggleGroup(g.key, kind)}
              aria-expanded={open}
              className="w-full px-4 py-2.5 rounded-xl border-2 bg-white dark:bg-slate-900 flex items-center justify-between gap-3 text-left text-sm font-semibold text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition cursor-pointer"
              style={{ borderColor: brandColor }}
            >
              <span className="flex items-center gap-2 min-w-0">
                <FolderTree className="w-4 h-4 shrink-0" style={{ color: brandColor }} />
                <span className="truncate">{g.label}</span>
                <span className="text-xs font-normal text-slate-500 dark:text-slate-400 shrink-0">{count} tétel</span>
              </span>
              {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
            </button>
            {open && <div className={kind === 'cards' ? 'space-y-3 pl-2 sm:pl-4' : 'space-y-2.5 pl-2 sm:pl-4'}>{g.items.map((r) => renderItem(r, true))}</div>}
          </div>
        );
      });
      if (kind === 'table') return <React.Fragment key={`n-${n.key}`}>{head}{groups}</React.Fragment>;
      // kártyás nézetben az alpontok a főpont kártyáján belül jelennek meg
      if (kind === 'cards' && groups.length > 0) {
        const inner = (
          <div className="pt-3 border-t border-dashed border-slate-200 dark:border-slate-700 space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Alpontok – {subInfo?.groups} csoport, {subInfo?.items} tétel
            </div>
            <div className="pl-3 border-l-2 space-y-2" style={{ borderColor: `${brandColor}55` }}>{groups}</div>
          </div>
        );
        if (n.rec) return <React.Fragment key={`n-${n.key}`}>{renderItem(n.rec, false, subInfo, inner)}</React.Fragment>;
        return (
          <div key={`n-${n.key}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-3">
            <h4 className="text-base font-bold text-slate-900 dark:text-white">{n.label}. pont</h4>
            {inner}
          </div>
        );
      }
      return (
        <React.Fragment key={`n-${n.key}`}>
          {head}
          {groups.length > 0 && (
            <div className="ml-4 sm:ml-8 pl-3 border-l-2 space-y-2" style={{ borderColor: `${brandColor}55` }}>{groups}</div>
          )}
        </React.Fragment>
      );
    });
  };

  // Helper for document tag badge
  const getFileTag = (filename: string, mime?: string) => {
    const ext = (filename || '').split('.').pop()?.toUpperCase() || 'DOK';
    if (mime === 'application/pdf' || ext === 'PDF') return 'PDF DOKUMENTUM';
    if (ext === 'XLSX' || ext === 'XLS') return 'XLSX TÁBLÁZAT';
    if (ext === 'DOCX' || ext === 'DOC') return 'DOCX DOKUMENTUM';
    return `${ext} DOKUMENTUM`;
  };

  // Parse external links from text
  const parseExternalLinks = (text: string) => {
    return String(text || '').split('\n').map(line => {
      const m = /^(.*)\s—\s(https?:\/\/\S+)$/.exec(line.trim());
      if (m) return { label: m[1].trim(), url: m[2].trim() };
      if (line.trim().startsWith('http')) return { label: line.trim(), url: line.trim() };
      return null;
    }).filter(Boolean);
  };

  const toPersonnel = (raw: any, fallbackTitle: string): PersonnelData => ({
    model: fromPublic(raw),
    settings: raw.settings || ORG_CHART_DEFAULTS,
    rootTitle: raw.settings?.root_title || raw.tenant_name || fallbackTitle,
    brandColor,
  });
  const liveBundle: LiveBundle | null = personnelRaw ? {
    own: personnelRaw.own ? toPersonnel(personnelRaw.own, tenant?.name || '') : null,
    entities: personnelRaw.entities
      ? Object.fromEntries(Object.entries(personnelRaw.entities).map(([id, raw]: [string, any]) => [Number(id), toPersonnel(raw, '')]))
      : null,
    linked: personnelRaw.linked ? {
      entities: personnelRaw.linked.entities || [],
      settings: personnelRaw.linked.settings,
      rootTitle: personnelRaw.linked.settings?.map?.root_title || personnelRaw.linked.tenant_name || tenant?.name || '',
      brandColor,
    } : null,
  } : null;

  return (
    <PersonnelDataProvider bundle={liveBundle}>
    <div className="min-h-screen bg-slate-50 dark:bg-[#070a12] text-slate-900 dark:text-slate-100 flex flex-col font-sans">
      {/* ── HEADER BANNER ────────────────────────────────────────── */}
      <header
        style={{
          background: `linear-gradient(135deg, ${brandColor} 0%, color-mix(in srgb, ${brandColor} 75%, #050814) 100%)`,
        }}
        className="text-white px-4 lg:px-8 py-8 shadow-md relative transition-colors"
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white/20 backdrop-blur-md border border-white/25 text-white inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Hivatalos Közzétételi Portál (2011. évi CXII. tv.)
              </span>
              {Boolean(tenant?.is_demo) && (
                <span className="px-3 py-0.5 rounded-full text-[11px] font-bold bg-amber-400 text-slate-950 inline-flex items-center gap-1.5 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-amber-900 animate-pulse" />
                  DEMO / BETANULÁSI KÖRNYEZET (NEM ÉLES ADATOK)
                </span>
              )}
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight drop-shadow-xs">
              {tenant?.name || branding.system_title || 'KözzéTér Pro.'}
            </h1>
            <p className="text-sm text-white/85 mt-1 max-w-2xl">
              Közzétételi lista – az információs önrendelkezési jogról és az információszabadságról szóló törvény melléklete alapján
            </p>

            {tenant?.header_left && (
              <div className="mt-3 text-xs text-white/75 space-y-0.5 font-mono">
                {tenant.header_left.split('\n').map((line: string, i: number) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2.5 self-end md:self-center">
            {availableTenants.length > 1 && (
              <select
                value={tenantSlug}
                onChange={(e) => {
                  setTenantSlug(e.target.value);
                  setSelectedPageSlug('');
                }}
                className="px-2.5 py-1.5 rounded-xl bg-white/20 hover:bg-white/25 border border-white/25 text-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer backdrop-blur-md"
              >
                {availableTenants.map(t => (
                  <option key={t.slug} value={t.slug} className="text-slate-900 bg-white dark:text-white dark:bg-slate-900">
                    {t.name}
                  </option>
                ))}
              </select>
            )}

            {/* Link Copy */}
            <button
              onClick={handleCopyLink}
              className="px-3 py-1.5 rounded-xl bg-white/15 hover:bg-white/25 border border-white/25 text-white text-xs font-medium backdrop-blur-md transition flex items-center gap-1.5 cursor-pointer shadow-xs"
              title="Publikus oldal hivatkozásának másolása"
            >
              {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copiedLink ? 'Másolva!' : 'Link másolása'}</span>
            </button>

            <ThemeToggle />

            {/* Admin Return */}
            {user && onBackToAdmin && (
              <button
                onClick={onBackToAdmin}
                className="px-3.5 py-1.5 rounded-xl bg-white text-slate-900 hover:bg-slate-100 font-semibold text-xs shadow-lg transition flex items-center gap-1.5 cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-indigo-600" />
                <span>Vissza a kezelőfelületre</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {Boolean(tenant?.is_demo) && (
        <div className="bg-amber-400 text-slate-950 font-bold px-4 py-2 text-center text-xs flex items-center justify-center gap-2 border-b border-amber-500 shadow-xs">
          <span className="w-2 h-2 rounded-full bg-slate-950 animate-ping" />
          <span>FIGYELEM: Ez egy demonstrációs és betanulási rendszer! Az itt szereplő adatok és nevek teszt jellegűek, nem minősülnek éles közzétételnek.</span>
        </div>
      )}

      {/* ── MAIN CONTENT ──────────────────────────────────────────── */}
      <main className="max-w-6xl w-full mx-auto px-4 lg:px-8 py-8 flex-1">
        {loading ? (
          <div className="py-24 text-center text-slate-500 flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
            <span className="text-sm">Közzétételi portál betöltése...</span>
          </div>
        ) : error ? (
          <div className="py-16 text-center text-red-600 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-2xl p-6">
            <h3 className="font-bold text-base mb-1">A közzétételi adatok nem érhetők el</h3>
            <p className="text-sm text-red-500">{error}</p>
          </div>
        ) : !selectedPageSlug ? (
          /* ── TENANT OVERVIEW: PORTAL TILES & SEARCH ─────────────── */
          <div className="space-y-8 animate-fade-in">
            {/* Published Pages Cards */}
            <div>
              <h2 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-500" />
                Közzétett Közadat Kategóriák
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pages.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPageSlug(p.slug)}
                    className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-500 dark:hover:border-indigo-500 rounded-2xl p-5 text-left transition-all duration-200 shadow-xs hover:shadow-md cursor-pointer flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-2xl p-2 rounded-xl bg-slate-100 dark:bg-slate-800 group-hover:scale-110 transition-transform">
                          {p.icon || '📋'}
                        </span>
                        <h3 className="font-bold text-base text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-cyan-400 transition-colors">
                          {p.title}
                        </h3>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                        {p.description || 'Közzétételi lista – automatikus import és felülvizsgálat alapján.'}
                      </p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs font-medium text-indigo-600 dark:text-cyan-400">
                      <span>Megtekintés →</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* ── KERESÉS ÉV SZERINT WIDGET ──────────────────────────── */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-cyan-400 flex items-center justify-center">
                  <Search className="w-4 h-4" />
                </div>
                <h3 className="font-bold text-base text-slate-900 dark:text-white">
                  Keresés év szerint
                </h3>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 max-w-2xl leading-relaxed">
                Válassz egy évet – megmutatjuk az adott évhez tartozó közzétett tételeket, és ha van hozzájuk feltöltött melléklet, egyben, egy ZIP fájlban letöltheted.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <select
                  value={selectedYear}
                  onChange={(e) => runYearFilter(e.target.value)}
                  className="px-3.5 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-xs font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[240px] cursor-pointer"
                >
                  <option value="">– Válassz évet ({availableYearsWithCount.length} elérhető év) –</option>
                  {availableYearsWithCount.map(item => (
                    <option key={item.year} value={item.year}>
                      {item.year}. év ({item.count} db tétel)
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => runYearFilter(selectedYear)}
                  disabled={!selectedYear || yearSearchLoading}
                  className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold text-xs transition cursor-pointer shadow-xs"
                >
                  Keresés
                </button>
              </div>

              {/* Year Search Results */}
              {selectedYear && (
                <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800 space-y-4 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                      Találatok a(z) <span className="font-bold text-indigo-600 dark:text-cyan-400">{selectedYear}</span>. évre: {yearSearchResults.length} db tétel
                    </div>

                    {matchesWithAttachments.length > 0 && (
                      <a
                        href={`/api/public/${encodeURIComponent(tenantSlug)}/attachments/export-zip?record_ids=${matchesWithAttachments.map(m => m.rec.id).join(',')}`}
                        download={`Kozadat_${tenantSlug}_${selectedYear}_mellekletek.zip`}
                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 hover:bg-indigo-100 text-indigo-700 dark:text-cyan-300 text-xs font-semibold border border-indigo-200 dark:border-indigo-800 transition shadow-xs"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Mellékletek letöltése ZIP-ben ({matchesWithAttachments.length})</span>
                      </a>
                    )}
                  </div>

                  {yearSearchResults.length === 0 ? (
                    <div className="py-6 text-center text-xs text-slate-400">
                      Nincs találat a kiválasztott évre.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                      {yearSearchResults.map(({ page, rec, d }, idx) => (
                        <div
                          key={idx}
                          onClick={() => setSelectedPageSlug(page.slug)}
                          className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/60 dark:border-slate-700/60 flex items-center justify-between gap-4 cursor-pointer transition"
                        >
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold text-indigo-600 dark:text-cyan-400 uppercase tracking-wider block">
                              {page.title} • {d.pont || `${rec.sort_order}. pont`}
                            </span>
                            <div className="text-xs font-semibold text-slate-900 dark:text-white truncate">
                              {d.megnevezes || d.cim || d.nev || 'Tétel'}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <PublicArchiveSection tenantSlug={tenantSlug} />
          </div>
        ) : (
          /* ── PAGE DETAIL: MULTI-LEVEL ACCORDION, CARDS, TABLE ───── */
          <div className="space-y-6 animate-fade-in">
            {/* Breadcrumb & Navigation */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <button
                onClick={() => {
                  setSelectedPageSlug('');
                  setFocusedBlockId(null);
                }}
                className="inline-flex items-center gap-2 text-xs font-semibold text-indigo-600 dark:text-cyan-400 hover:underline cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>← {tenant?.name || 'Kezdőlap'} – közzétételi lista</span>
              </button>

              {/* View mode switcher & Admin actions */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Search input */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    placeholder="Keresés az oldalon..."
                    value={pageSearch}
                    onChange={(e) => setPageSearch(e.target.value)}
                    className="pl-8 pr-3 py-1.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 w-48 sm:w-56"
                  />
                </div>

                {/* 3 View Mode buttons */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-1 flex items-center gap-1 shadow-xs">
                  <button
                    onClick={() => setViewModeOverride('accordion')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                      effectiveDisplayMode === 'accordion'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    }`}
                    title="Kinyíló listás nézet (Harmonika - Alapértelmezett)"
                  >
                    <ListTree className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Kinyíló lista</span>
                  </button>

                  <button
                    onClick={() => setViewModeOverride('cards')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                      effectiveDisplayMode === 'cards'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    }`}
                    title="Kártyás lista"
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Kártyák</span>
                  </button>

                  <button
                    onClick={() => setViewModeOverride('table')}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                      effectiveDisplayMode === 'table'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    }`}
                    title="Táblázatos nézet"
                  >
                    <TableIcon className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Táblázat</span>
                  </button>
                </div>

                {/* Alpontok szerint / Egyszerű lista */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-1 flex items-center gap-1 shadow-xs" role="group" aria-label="Alpontok megjelenítése">
                  {([['tree', 'Alpontok szerint', FolderTree], ['flat', 'Egyszerű lista', Rows3]] as const).map(([key, label, Icon]) => (
                    <button key={key} type="button" onClick={() => setHierarchyMode(key)} aria-pressed={hierarchyMode === key}
                      title={key === 'tree' ? 'Az alpontok (pl. ügyfajták) csoportosítva, a szülőpont alatt' : 'Minden tétel egy listában'}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                        hierarchyMode === key ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}>
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden md:inline">{label}</span>
                    </button>
                  ))}
                </div>

                {/* Táblázat rácsvonalai (csak táblázat nézetben) */}
                {effectiveDisplayMode === 'table' && (
                  <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-1 flex items-center gap-1 shadow-xs" role="group" aria-label="Táblázat rácsvonalai">
                    <span className="px-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400 hidden md:inline">Rács:</span>
                    {([['rows', 'Sorok', 'Csak a sorok között vízszintes vonal'], ['grid', 'Teljes', 'Minden cella körül vonal (rácsos táblázat)'], ['none', 'Nincs', 'Vonalak nélkül, sávozott sorokkal']] as const).map(([key, label, title]) => (
                      <button key={key} type="button" onClick={() => setGridOverride(key)} aria-pressed={effectiveTableGrid === key} title={title}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                          effectiveTableGrid === key ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}>
                        {key === 'grid' ? <Grid3x3 className="w-3.5 h-3.5" /> : key === 'rows' ? <Rows3 className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                        <span className="hidden md:inline">{label}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Admin-only Save Default View & Batch Upload */}
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveDefaultViewMode}
                      disabled={isSavingViewMode}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 border transition cursor-pointer ${
                        savedModeSuccess
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300'
                          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:border-blue-500'
                      }`}
                      title="Kiválasztott megjelenítési mód mentése alapértelmezettként az oldalhoz"
                    >
                      {savedModeSuccess ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Save className="w-3.5 h-3.5 text-blue-600" />}
                      <span className="hidden lg:inline">{savedModeSuccess ? 'Nézet mentve!' : 'Nézet mentése'}</span>
                    </button>

                    <button
                      onClick={() => setIsBatchModalOpen(true)}
                      className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                      title="Kötegelt fájlfeltöltés elnevezési konvenció alapján több ponthoz egyszerre"
                    >
                      <FolderOpen className="w-3.5 h-3.5" />
                      <span>📂 Kötegelt feltöltés</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Page Header Title */}
            <div className="border-b border-slate-200 dark:border-slate-800 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2.5">
                  <span>{activePage?.icon || '🏛️'}</span>
                  <span>{activePage?.title}</span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {activePage?.description || '1–5. pont – közzétételi alapadatok és szabályzatok'}
                </p>
              </div>

              {/* Focused block banner */}
              {focusedBlockId && (
                <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-900 px-3 py-1.5 rounded-xl text-xs text-blue-800 dark:text-blue-300">
                  <Filter className="w-3.5 h-3.5" />
                  <span>Különálló blokk nézet aktív</span>
                  <button
                    onClick={() => setFocusedBlockId(null)}
                    className="underline font-bold ml-1 hover:text-blue-900 cursor-pointer"
                  >
                    Összes mutatása
                  </button>
                </div>
              )}
            </div>

            {/* Year selection and Chronological sort bar - csak ha van választható év az oldalon */}
            {pageSelectableYears.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-2.5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1 shrink-0 mr-1">
                    <Calendar className="w-3.5 h-3.5 text-blue-600 dark:text-cyan-400" /> Év:
                  </span>
                  <button
                    type="button"
                    onClick={() => setPageSelectedYear('all')}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition cursor-pointer shrink-0 whitespace-nowrap ${
                      pageSelectedYear === 'all'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    Összes év ({records.length})
                  </button>

                  {/* Évválasztó lista (dropdown) */}
                  <div className="relative inline-flex items-center">
                    <select
                      value={pageSelectedYear !== 'all' ? pageSelectedYear : ''}
                      onChange={(e) => {
                        if (e.target.value === 'all') {
                          setPageSelectedYear('all');
                        } else if (e.target.value) {
                          setPageSelectedYear(e.target.value);
                        }
                      }}
                      className={`text-xs font-semibold pl-3 pr-8 py-1.5 rounded-xl border appearance-none cursor-pointer transition focus:outline-hidden focus:ring-2 focus:ring-blue-500/30 ${
                        pageSelectedYear !== 'all'
                          ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      <option value="" disabled={pageSelectedYear !== 'all'} className="bg-white dark:bg-slate-800 text-slate-500">
                        {pageSelectedYear !== 'all'
                          ? `${pageSelectedYear}. év kiválasztva`
                          : '📅 Válassz évet a listából...'}
                      </option>
                      <option value="all" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">
                        Összes év ({records.length})
                      </option>
                      {pageSelectableYears.map(({ year: yr, count }) => (
                        <option key={yr} value={String(yr)} className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">
                          {yr}. év ({count})
                        </option>
                      ))}
                    </select>
                    <ChevronDown className={`w-3.5 h-3.5 absolute right-2.5 pointer-events-none transition-colors ${
                      pageSelectedYear !== 'all' ? 'text-white' : 'text-slate-400'
                    }`} />
                  </div>
                </div>

              {/* Chronological sort toggle */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => {
                    setPageSortOrder(prev => {
                      if (prev === 'pont') return 'year_desc';
                      if (prev === 'year_desc') return 'year_asc';
                      return 'pont';
                    });
                  }}
                  className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                  title="Sorrend: pontok szerint vagy időrendben csökkenő/növekvő"
                >
                  <ArrowUpDown className="w-3.5 h-3.5 text-blue-600 dark:text-cyan-400" />
                  <span>
                    {pageSortOrder === 'year_desc'
                      ? 'Időrend: Újabb elöl (2026 ➔ 2024)'
                      : pageSortOrder === 'year_asc'
                      ? 'Időrend: Régebbi elöl (2024 ➔ 2026)'
                      : 'Sorrend: Pontok szerint'}
                  </span>
                </button>
              </div>
            </div>
            )}

            {pageDetailLoading ? (
              <div className="py-20 text-center text-slate-500 flex flex-col items-center gap-3">
                <RefreshCw className="w-7 h-7 animate-spin text-blue-600" />
                <span className="text-xs">Oldal adatainak betöltése...</span>
              </div>
            ) : sortedRecords.length === 0 ? (
              <div className="py-16 text-center text-slate-500 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
                Nincs a keresésnek megfelelő közzétett adat.
              </div>
            ) : effectiveDisplayMode === 'accordion' ? (
              /* ── 1. KINYÍLÓ LISTÁS NÉZET (HARMONIKA - ALAPÉRTELMEZETT, 1., 2. ÉS 3. KÉPEK MINTÁJÁRA) ── */
              <div className="space-y-2.5">
                {renderGrouped(sortedRecords, 'accordion', (rec, nested, subInfo) => {
                  const d = parseRecordData(rec);

                  const nodeId = String(rec.id);
                  const isNodeOpen = expandedNodes[nodeId] ?? false;
                  const pont = d.pont || `${rec.sort_order || 1}. pont`;
                  const megnevezes = d.megnevezes || d.cim || d.nev || '–';

                  // Header title styling
                  // csoporton belül elég a tétel neve (a pont és a csoport a sávon látszik)
                  const headerTitle = nested ? megnevezes : `${pont} — ${megnevezes}`;
                  const externalLinks = parseExternalLinks(d.kulso_linkek);
                  const attachments = rec.attachments || [];

                  return (
                    <div
                      key={rec.id}
                      className="rounded-xl overflow-hidden shadow-xs border border-transparent dark:border-slate-800/80"
                    >
                      {/* LEVEL 1: TOP-LEVEL BLUE BAR (MINTA KÉP 1 & 2) */}
                      <button
                        type="button"
                        onClick={() => toggleNode(nodeId)}
                        style={{ backgroundColor: brandColor }}
                        className="w-full px-4 py-3.5 flex items-center justify-between text-left text-white font-semibold text-sm hover:brightness-105 active:brightness-95 transition cursor-pointer shadow-xs gap-4 rounded-xl"
                      >
                        <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap leading-snug">
                          <span>{headerTitle}</span>
                          {rec.year ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white border border-white/30 backdrop-blur-xs">
                              {rec.year}. év
                            </span>
                          ) : null}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {subInfo && (
                            <span className="px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-semibold flex items-center gap-1 whitespace-nowrap"
                              title={isNodeOpen ? 'Az alpontok a pont alatt láthatók' : 'Nyisd le a pontot az alpontok megtekintéséhez'}>
                              <FolderTree className="w-3 h-3" />
                              {subInfo.groups} alcsoport · {subInfo.items} tétel
                            </span>
                          )}
                          {attachments.length > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-mono flex items-center gap-1">
                              <Paperclip className="w-3 h-3" />
                              {attachments.length}
                            </span>
                          )}
                          <span className="text-white/80 transition-transform">
                            {isNodeOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </span>
                        </div>
                      </button>

                      {/* LEVEL 1 EXPANDED PANEL */}
                      {isNodeOpen && (
                        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 border-t-0 rounded-b-xl p-5 space-y-4">
                          
                          {/* Block Actions Bar (Link & Single block focus) */}
                          <div className="flex items-center justify-between text-[11px] text-slate-400 border-b border-slate-100 dark:border-slate-800 pb-2">
                            <span className="font-mono">{pont} adatai és mellékletei</span>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => setFocusedBlockId(nodeId)}
                                className="hover:text-blue-600 dark:hover:text-blue-400 transition cursor-pointer"
                                title="Fókuszálás erre a blokkra külön nézetként"
                              >
                                [ Blokk nézet ]
                              </button>
                              <button
                                onClick={() => {
                                  const url = new URL(window.location.href);
                                  const isKpub = window.location.hostname.includes('kpub');
                                  if (isKpub) {
                                    url.pathname = `/${encodeURIComponent(tenantSlug)}`;
                                    url.searchParams.delete('t');
                                  } else {
                                    url.searchParams.set('t', tenantSlug);
                                  }
                                  url.searchParams.set('p', selectedPageSlug);
                                  url.searchParams.set('block', nodeId);
                                  navigator.clipboard.writeText(url.toString());
                                  alert('Blokk hivatkozása a vágólapra másolva!');
                                }}
                                className="hover:text-blue-600 dark:hover:text-blue-400 transition cursor-pointer flex items-center gap-1"
                              >
                                <Share2 className="w-3 h-3" />
                                <span>Megosztás</span>
                              </button>
                            </div>
                          </div>

                          {/* STRUCTURED TRANSPARENCY FIELDS (1. KÉP MINTÁJÁRA) */}
                          <div className="space-y-3 text-xs leading-relaxed">
                            {fields
                              .filter(f => f.public_visible !== 0 && f.name !== 'pont' && f.name !== 'kulso_linkek')
                              .map((f) => {
                                const val = d?.[f.name];
                                if (val === undefined || val === null || String(val).trim() === '') return null;
                                // formázottan mentett leírás: szűrt HTML, a címke külön sorban
                                if (f.name === 'leiras' && (d?.leiras_html || hasPersonnelToken(String(val)))) {
                                  return (
                                    <div key={f.id} className="text-slate-800 dark:text-slate-200">
                                      <b className="text-slate-900 dark:text-white font-semibold block mb-1">{f.public_label || f.label}:</b>
                                      <RichTextView html={d.leiras_html} text={String(val)} />
                                    </div>
                                  );
                                }
                                return (
                                  <div key={f.id} className="text-slate-800 dark:text-slate-200">
                                    <b className="text-slate-900 dark:text-white font-semibold">{f.public_label || f.label}:</b>{' '}
                                    <span className="whitespace-pre-wrap">{String(val)}</span>
                                  </div>
                                );
                              })}
                          </div>

                          {/* EXTERNAL LINKS AS CARDS */}
                          {externalLinks.length > 0 && (
                            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                              {externalLinks.map((link: any, lIdx: number) => (
                                <div
                                  key={lIdx}
                                  className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-3 flex items-center justify-between gap-3"
                                >
                                  <div>
                                    <h5 className="font-bold text-xs text-slate-900 dark:text-white">
                                      {link.label}
                                    </h5>
                                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 uppercase">
                                      KÜLSŐ HIVATKOZÁS
                                    </span>
                                  </div>
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition shrink-0"
                                  >
                                    <span>Megnyitás</span>
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* JOGSZABÁLYI HIVATKOZÁSOK (TÖRVÉNYI ALAP) */}
                          {rec.regulations && rec.regulations.length > 0 && (
                            <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                                Törvényi alap és jogszabályi háttér ({rec.regulations.length})
                              </span>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {rec.regulations.map((reg: any) => (
                                  <a
                                    key={reg.id}
                                    href={reg.njt_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-amber-50/70 hover:bg-amber-100/80 dark:bg-amber-950/40 dark:hover:bg-amber-900/50 border border-amber-200/80 dark:border-amber-800/60 rounded-xl p-2.5 flex items-center justify-between gap-3 text-xs text-amber-950 dark:text-amber-200 transition shadow-xs group"
                                    title={`${reg.title} (${reg.code}) – Nemzeti Jogtár`}
                                  >
                                    <div className="min-w-0 flex items-center gap-2">
                                      <BookOpen className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                                      <div className="min-w-0">
                                        <span className="font-bold block leading-tight">{reg.short_title || reg.code}</span>
                                        <span className="text-[10px] text-amber-700/80 dark:text-amber-400/80 block truncate max-w-[200px]">{reg.title}</span>
                                      </div>
                                    </div>
                                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-200/60 dark:bg-amber-800/40 px-2 py-0.5 rounded shrink-0">
                                      <span>njt.hu</span>
                                      <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                                    </span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* EMBEDDED DOCUMENT CARDS (2. ÉS 3. KÉP MINTÁJÁRA) */}
                          {attachments.length > 0 && (
                            <div className="space-y-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                                Csatolt dokumentumok és mellékletek ({attachments.length})
                              </span>

                              <div className="space-y-2">
                                {attachments.map((att: AttachmentItem) => (
                                  <div
                                    key={att.id}
                                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 rounded-xl p-3 flex items-center justify-between gap-3 shadow-xs hover:border-blue-400 dark:hover:border-blue-500 transition"
                                  >
                                    <div className="min-w-0 flex-1">
                                      <h5 className="font-bold text-xs text-slate-900 dark:text-white truncate">
                                        {att.filename}
                                      </h5>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200/60 dark:border-blue-900/40">
                                          {getFileTag(att.filename, att.mime_type)}
                                        </span>
                                        {att.size && (
                                          <span className="text-[10px] text-slate-400 font-mono">
                                            {Math.round(att.size / 1024)} KB
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0">
                                      <a
                                        href={`/api/public/${encodeURIComponent(tenantSlug)}/attachments/${att.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-xs flex items-center gap-1.5 transition"
                                      >
                                        <span>Megnyitás</span>
                                      </a>

                                      {isAdmin && (
                                        <button
                                          onClick={() => handleDeleteAttachment(att.id, att.filename)}
                                          className="p-1.5 text-neutral-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition"
                                          title="Csatolmány törlése"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* DIRECT UPLOAD ZONE: BÁRHOVÁ FÁJLFELTÖLTÉS (EGY VAGY TÖBB FÁJL EGYSZERRE) */}
                          {isAdmin && (
                            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                              <label className="border border-dashed border-slate-300 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-400 bg-slate-50/60 dark:bg-slate-800/40 rounded-xl p-3 flex items-center justify-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition">
                                <input
                                  type="file"
                                  multiple
                                  disabled={uploadingRecordId === rec.id}
                                  onChange={(e) => handleDirectUpload(rec.id, e.target.files)}
                                  className="hidden"
                                />
                                {uploadingRecordId === rec.id ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                                    <span>Feltöltés folyamatban...</span>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 text-blue-600" />
                                    <span>📎 Új fájl(ok) feltöltése ehhez a ponthoz (több fájl is kijelölhető egyszerre)</span>
                                  </>
                                )}
                              </label>
                            </div>
                          )}

                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : effectiveDisplayMode === 'cards' ? (
              /* ── 2. KÁRTYÁS BLOKKOS NÉZET ── */
              <div className="space-y-4">
                {renderGrouped(sortedRecords, 'cards', (rec, _nested, _subInfo, inner) => {
                  const d = parseRecordData(rec);
                  const pont = d.pont || `${rec.sort_order || 1}. pont`;
                  const megnevezes = d.megnevezes || d.cim || d.nev || '–';

                  return (
                    <div
                      key={rec.id}
                      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs space-y-3"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-blue-600 dark:text-cyan-400">
                              {pont}
                            </span>
                            {rec.year ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-cyan-300 border border-indigo-200 dark:border-indigo-800">
                                {rec.year}. év
                              </span>
                            ) : null}
                          </div>
                          <h4 className="text-base font-bold text-slate-900 dark:text-white leading-snug">
                            {megnevezes}
                          </h4>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs pt-1">
                        {fields.filter(f => f.public_visible !== 0 && f.name !== 'pont' && f.name !== 'megnevezes').map((f) => {
                          const val = d[f.name];
                          if (val === undefined || val === null || val === '') return null;
                          return (
                            <div key={f.id} className="space-y-0.5">
                              <span className="font-semibold text-slate-500 dark:text-slate-400 block">
                                {f.public_label || f.label}
                              </span>
                              {f.name === 'leiras' && (d.leiras_html || hasPersonnelToken(String(val))) ? (
                                <RichTextView html={d.leiras_html} text={String(val)} className="text-slate-800 dark:text-slate-200" />
                              ) : (
                                <div className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                                  {String(val)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Mellékletek */}
                      {rec.attachments && rec.attachments.length > 0 && (
                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                          {rec.attachments.map((att: any) => (
                            <div key={att.id} className="flex items-center justify-between text-xs p-2 rounded-lg bg-slate-50 dark:bg-slate-800">
                              <span className="truncate max-w-[280px] font-medium">{att.filename}</span>
                              <a
                                href={`/api/public/${encodeURIComponent(tenantSlug)}/attachments/${att.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 font-semibold hover:underline"
                              >
                                Megnyitás →
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                      {inner}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* ── 3. TÁBLÁZATOS NÉZET ── */
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-xs">
                <div className="overflow-x-auto">
                  <table className={`w-full text-left text-xs ${
                    effectiveTableGrid === 'grid'
                      ? 'border-collapse [&_th]:border [&_td]:border [&_th]:border-slate-200 [&_td]:border-slate-200 dark:[&_th]:border-slate-700 dark:[&_td]:border-slate-700'
                      : effectiveTableGrid === 'none'
                        ? '[&_tbody>tr:nth-child(even)]:bg-slate-50/70 dark:[&_tbody>tr:nth-child(even)]:bg-slate-800/30'
                        : ''}`}>
                    <thead className="bg-slate-50 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3 font-bold w-24">Pont</th>
                        <th className="px-3 py-3 font-bold w-20 text-center">Év</th>
                        <th className="px-4 py-3 font-bold">Megnevezés</th>
                        <th className="px-4 py-3 font-bold">Tartalom / Érték</th>
                        <th className="px-4 py-3 font-bold w-40">Melléklet</th>
                      </tr>
                    </thead>
                    <tbody className={effectiveTableGrid === 'rows' ? 'divide-y divide-slate-100 dark:divide-slate-800/60' : ''}>
                      {renderGrouped(sortedRecords, 'table', (rec, nested) => {
                        const d = parseRecordData(rec);
                        const pont = d.pont || `${rec.sort_order}. pont`;
                        const megnevezes = d.megnevezes || d.cim || d.nev || '–';
                        const ertek = d.ertek || d.leiras || d.tartalom || '–';

                        return (
                          <tr key={rec.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition">
                            <td className="px-4 py-3 font-semibold text-blue-600 dark:text-cyan-400 whitespace-nowrap" style={nested ? { paddingLeft: 40 } : undefined}>
                              {nested ? <span className="text-slate-400 font-normal" title={pont}>└ {String(pont).split('›').pop()?.trim()}</span> : pont}
                            </td>
                            <td className="px-3 py-3 text-center whitespace-nowrap">
                              {rec.year ? (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-cyan-300 border border-indigo-200 dark:border-indigo-800">
                                  {rec.year}
                                </span>
                              ) : (
                                <span className="text-[10px] text-slate-400 font-mono">Ált.</span>
                              )}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-white max-w-xs">
                              {megnevezes}
                            </td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-md">
                              <RichTextView html={d.leiras_html} text={ertek} />
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {rec.attachments && rec.attachments.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                  {rec.attachments.map((att: any) => (
                                    <a
                                      key={att.id}
                                      href={`/api/public/${encodeURIComponent(tenantSlug)}/attachments/${att.id}`}
                                      className="inline-flex items-center gap-1.5 text-blue-600 dark:text-cyan-400 hover:underline"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      <Paperclip className="w-3 h-3" />
                                      <span className="truncate max-w-[140px]">{att.filename}</span>
                                    </a>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <PublicArchiveSection tenantSlug={tenantSlug} pageId={activePage?.id} />
          </div>
        )}
      </main>

      {/* ── BATCH UPLOAD MODAL ────────────────────────────────────── */}
      {activePage && (
        <BatchUploadModal
          isOpen={isBatchModalOpen}
          onClose={() => setIsBatchModalOpen(false)}
          pageId={activePage.id}
          pageTitle={activePage.title}
          records={records}
          onUploadComplete={() => loadPageDetail(tenantSlug, selectedPageSlug)}
        />
      )}

      {/* ── FOOTER ───────────────────────────────────────────────── */}
      <footer className="mt-auto border-t border-slate-200 dark:border-slate-800 py-6 px-4 text-center text-xs text-slate-400 bg-white dark:bg-[#0b0f1a]">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div>
            {tenant?.name ? `${tenant.name} – ` : ''}{branding.system_title || 'KözzéTér Pro.'} Közzétételi Rendszer
          </div>
          <div className="font-mono text-[11px] text-slate-500">
            2011. évi CXII. törvény (Infotv.) szerinti megfelelőség
          </div>
        </div>
      </footer>

      {/* Lebegő "Vissza a tetejére" gomb nyilvános nézetekhez */}
      <ScrollToTopButton containerSelector="main" />
    </div>
    </PersonnelDataProvider>
  );
};
